import {
  REVIEWED_CARD_COPY,
  REVIEWED_ROLE_COPY,
  type ReviewedCardCopy,
  type ReviewedRoleCopy,
} from "./reviewed-copy.ts";

// 悬停解释：名词与机制的一句话说明。
//
// 注意事项：文案只讲规则事实（与 Core 的 docs/engine.md 及权威内容定义一致），不写实现细节，也不提示隐藏信息。

export const GLOSSARY = {
  damage: "伤害值：累积到角色卡的体力（HP）即退场；回复会从伤害值里扣。",
  dice: "骰目：掷 4 面骰 + 6 面骰，合计等于场所卡上的数字就停在那里；合计 7 可以自己挑场所。",
  reroll: "掷出的骰目落在自己所在的场所时那次作废，重新掷（不可以原地不动）。",
  adjacent: "相邻＝同一區域里的另一张场所卡；相邻移动不掷骰，但只有八云紫与博丽灵梦可以。",
  area: "三大區域各由棋盘上成组的两张场所卡组成；攻击范围与相邻移动都以当前區域分组为准。",
  equipment: "装备牌：抽到后留在装备区常驻，例如绯想之剑让攻击伤害 +1。",
  kleinji: "命莲寺卡：先进入抽卡者手牌，再由抽卡者拖给一名玩家；收卡者决定遵从指示还是宣言「什么都不会发生」。",
  judge: "審判：四季映姬相关的标记。",
  reveal: "公開身份：翻开自己的角色卡；翻开后能力与胜利条件对所有人可见。",
  hidden: "未公開的身份不可查看——身份卡只在公开或退场后才亮出来（规则如此，前端也拿不到）。",
  pass: "放弃这一步：场所行动与攻击可以先不做。",
  attack: "攻击：选择同一區域内的一名其他玩家，掷四面骰与六面骰，通常伤害为两颗骰子点数之差的绝对值。",
  round: "第一轮与之后的回合一样可以攻击射程内目标。",
  hime: "公主与米：3～5 人局时额外生效的调整规则。",
  play: "出牌区：你手上的装备与状态都摆在这里，拖到目标身上即可使用。",
} as const;

export interface CopyEntry {
  name: string;
  effect: string;
}

/** ABILITY_COPY 把协议能力 id 翻译成玩家可读的名称与完整效果。 */
export const ABILITY_COPY: Record<string, CopyEntry> = {
  cirno_diamond_blizzard: {
    name: "雪符「钻石风暴」",
    effect: "若可以攻击则必须攻击。",
  },
  cirno_freeze_atmosphere: {
    name: "冻符「急冻大气」",
    effect: "伤害值达到 9 以上时可以立刻公开身份；若因此公开，将伤害值归零。",
  },
  kaguya_kinkakuji: {
    name: "新难题「金阁寺的天花板」",
    effect: "有玩家死亡时，可以获取其一件装备；若由你击杀，可以获得其全部装备。",
  },
  komachi_higan_retour: {
    name: "死神「彼岸归航」",
    effect: "攻击后对自己造成 2 点伤害，再攻击一次刚才的目标。",
  },
  eirin_life_game: {
    name: "苏活「生命游戏」",
    effect: "仅限一次：将一名玩家的伤害值调到 7。",
  },
  eirin_rising_game: {
    name: "苏生「Rising Game」",
    effect: "不受博丽神社卡和永远亭影响；其他玩家受到这些效果时，你也可以受到相同影响。",
  },
  eirin_chuuten_no_tenchi: {
    name: "天丸「壶中的天地」",
    effect: "仅限一次：回合开始时，可以把胜利条件改为「左邻玩家获胜」。",
  },
  kanako_kami_no_kayu: {
    name: "筒粥「神之粥」",
    effect: "非因通常能力公开且持有「扩展御柱」时，胜利条件追加「游戏结束时身处守矢神社所在领域」。",
  },
  parsee_jealousy: {
    name: "花开爷爷「对华丽的仁者之嫉妒」",
    effect: "回合开始时，若伤害值不是所有角色中最低，可以回复 1 点伤害值。",
  },
  reimu_dream_seal: {
    name: "灵符「梦想封印」",
    effect: "仅限一次：攻击时，可以调整其中一个骰子的结果。",
  },
  reimu_dream_seal_flash: {
    name: "神灵「梦想封印瞬」",
    effect: "仅限一次：移动时可以放弃投骰，改为移动到相邻场所。",
  },
  marisa_master_spark: {
    name: "恋符「Master Spark」",
    effect: "回合开始时，可以投掷六面骰，对一名其他玩家造成等量伤害并累计点数；累计超过 6 后失效。",
  },
  sakuya_eternal_meek: {
    name: "奇术「永恒的温柔」",
    effect: "仅限一次：回合开始时，选择对一名其他玩家造成 1d4 伤害，或获取场上的一件装备。",
  },
  youmu_satellite_slash: {
    name: "「待宵反射卫星斩」",
    effect: "受到攻击伤害后，可以立即反击伤害来源；反击不能使用装备，濒死时不能发动。",
  },
  sanae_miracle_fruit: {
    name: "奇迹「神秘果」",
    effect: "攻击时选择正常攻击，或为一名玩家回复等于本次攻击伤害的伤害值。",
  },
  remilia_vampire_fantasy: {
    name: "神术「吸血鬼幻想」",
    effect: "你的攻击造成伤害后，回复 2 点伤害值。",
  },
  yuyuko_bloom: {
    name: "樱符「完全墨染的樱花」",
    effect: "可以追加进行 X 个回合，X 为死亡玩家数量。",
  },
  yukari_shinobi: {
    name: "紫奥义「弹幕结界」",
    effect: "你的攻击不受领域限制。",
  },
  utsuho_mega_flare: {
    name: "爆符「千兆耀斑」",
    effect: "回合内指定一个场所，对其中所有玩家造成 1 点伤害。",
  },
  utsuho_nuclear_rampage: {
    name: "核热「核反应执御不能」",
    effect: "仅限一次：投掷两枚骰子，对点数之和对应场所的所有玩家造成 3 点伤害；投掷后可以防止此伤害。",
  },
  nue_unknown_seed: {
    name: "通常能力：正体不明",
    effect: "可以不遵循命莲寺卡的指示。",
  },
  nue_ryuuji_bow: {
    name: "恨弓「源三位赖政之弓」",
    effect: "回合内可以对一名其他玩家造成 1 点伤害；本局第一次使用时可以改为回复。",
  },
};

/** 角色卡公开或本人查看时，用它补上角色卡图里难以辨认的能力说明。 */
export const ROLE_ABILITIES: Record<string, string[]> = {
  cirno: ["cirno_diamond_blizzard", "cirno_freeze_atmosphere"],
  kaguya: ["kaguya_kinkakuji"],
  komachi: ["komachi_higan_retour"],
  eirin: ["eirin_life_game", "eirin_rising_game", "eirin_chuuten_no_tenchi"],
  kanako: ["kanako_kami_no_kayu"],
  parsee: ["parsee_jealousy"],
  reimu: ["reimu_dream_seal", "reimu_dream_seal_flash"],
  marisa: ["marisa_master_spark"],
  sakuya: ["sakuya_eternal_meek"],
  youmu: ["youmu_satellite_slash"],
  sanae: ["sanae_miracle_fruit"],
  remilia: ["remilia_vampire_fantasy"],
  yuyuko: ["yuyuko_bloom"],
  yukari: ["yukari_shinobi"],
  utsuho: ["utsuho_mega_flare", "utsuho_nuclear_rampage"],
  nue: ["nue_unknown_seed", "nue_ryuuji_bow"],
};

/** CARD_COPY 来自全部通过人工审核的实体卡面文字。 */
export const CARD_COPY: Readonly<Record<string, ReviewedCardCopy>> = REVIEWED_CARD_COPY;

const ROLE_COPY: Readonly<Record<string, ReviewedRoleCopy>> = REVIEWED_ROLE_COPY;

export function roleCopy(id: string): ReviewedRoleCopy | undefined {
  return ROLE_COPY[id];
}

export function cardCopy(id: string): ReviewedCardCopy | undefined {
  return CARD_COPY[id];
}

export function abilityName(id: string): string {
  return ABILITY_COPY[id]?.name ?? "未识别能力";
}

export function abilityTip(id: string): string {
  const copy = ABILITY_COPY[id];
  return copy ? `${copy.name}｜${copy.effect}` : "能力资料尚未收录。";
}

export function roleAbilityTip(roleID: string): string {
  const reviewed = roleCopy(roleID);
  if (reviewed) return reviewed.detail;
  const entries = (ROLE_ABILITIES[roleID] ?? [])
    .map((id) => ABILITY_COPY[id])
    .filter((entry): entry is CopyEntry => entry !== undefined);
  return entries.length > 0
    ? entries.map((entry) => `${entry.name}：${entry.effect}`).join("\n")
    : "该角色没有已收录的特殊能力。";
}

export function cardName(id: string): string {
  return CARD_COPY[id]?.name ?? "未识别卡牌";
}

export function cardEffect(id: string, observedText?: string): string {
  return CARD_COPY[id]?.effect ?? (observedText && observedText !== id ? observedText : "效果尚未公开。");
}

export function cardTip(id: string, observedText?: string): string {
  return `${CARD_COPY[id]?.name ?? "卡牌"}｜${cardEffect(id, observedText)}`;
}

export const PROMPT_LABEL: Record<string, string> = {
  choose_place: "选择移动地点",
  choose_player: "选择一名玩家",
  choose_place_card: "选择一个场所",
  choose_deck: "选择卡组",
  choose_mode: "选择效果",
  choose_equipment: "选择一件装备",
  choose_dice: "调整骰子",
  yes_no: "是否发动",
  klein_resolve: "处理命莲寺卡",
};

export const MODE_LABEL: Record<string, string> = {
  basic: "基礎規則",
  normal: "通常",
  hard: "困難",
  lunatic: "極難",
};

/** PLACE_EFFECT 是六处场所的场所行动效果（见 touranki-core/internal/content/places.go，写在场地卡上）。 */
export const PLACE_EFFECT: Record<string, string> = {
  hakurei: "抽一张博丽神社卡并遵从",
  moriya: "抽一张守矢神社卡并遵从",
  kleinji: "抽一张命莲寺卡放入手牌，再交给一名玩家",
  human_village: "自选一种卡组，效果同对应场所",
  eientei: "对一名玩家造成 2 点伤害，或回复 1 点",
  magic_forest: "夺取另一名玩家的一件装备",
};

export type GlossaryKey = keyof typeof GLOSSARY;
